import { useDeleteAuthSession } from "@boundless/bl-api-client/allauth/auth"
import {
  prefetchCommonUserRetrieveQuery,
  useCommonUserRetrieveSuspense,
} from "@boundless/bl-api-client/common/services"
import { useQueryClient } from "@tanstack/react-query"
import {
  createFileRoute,
  linkOptions,
  Outlet,
  redirect,
} from "@tanstack/react-router"
import mixpanel from "mixpanel-browser"
import { useStore } from "zustand"

import DashboardLayout from "@/components/layout/DashboardLayout"
import { getAuthClient, setSessionToken } from "@/lib/clients"
import { markManualLogout, useSessionWatcher } from "@/lib/session"
import { store } from "@/lib/store"
import { redirectTo } from "@/lib/utils"

export const Route = createFileRoute("/_dashboard")({
  beforeLoad: ({ context: { store }, location }) => {
    if (!store.getState().isAuthenticated) {
      throw redirect({
        to: "/login",
        search: {
          redirect: location.href,
        },
      })
    }
  },
  loader: async ({ context: { queryClient } }) => {
    await prefetchCommonUserRetrieveQuery(queryClient)
  },
  component: DashboardView,
})

function DashboardView() {
  const isAuthenticated = useStore(store, (state) => state.isAuthenticated)

  if (!isAuthenticated) {
    return null
  }

  return <DashboardViewAuthenticated />
}

function DashboardViewAuthenticated() {
  const client = getAuthClient()
  const navigate = Route.useNavigate()
  const queryClient = useQueryClient()

  const { data: user } = useCommonUserRetrieveSuspense()

  const toLoginOptions = user.partnerSlug
    ? linkOptions({ to: "/p/$slug/login", params: { slug: user.partnerSlug } })
    : linkOptions({ to: "/login" })

  const { mutate: onLogout } = useDeleteAuthSession({
    mutation: {
      // Restored original admin session
      onSuccess: (session) => {
        setSessionToken(null)
        if (session.data.user.redirect) {
          redirectTo(session.data.user.redirect)
        }
      },
      // Proper logout
      onError: (error) => {
        setSessionToken(null)
        if (!error.response) {
          throw error.response
        }
        navigate(toLoginOptions)
      },
      onSettled: async () => {
        store.getState().resetAuth()
        await queryClient.cancelQueries()
        queryClient.clear()
        mixpanel.reset()
      },
    },
  })

  useSessionWatcher(onLogout)

  const manualLogout = (): void => {
    markManualLogout()
    onLogout({ client })
  }

  return (
    <DashboardLayout onLogout={manualLogout}>
      <Outlet />
    </DashboardLayout>
  )
}
